import { useState, useMemo, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Filter, Search } from "lucide-react";
import DatePicker from "react-datepicker";
import { format } from "date-fns";
import "react-datepicker/dist/react-datepicker.css";
import DashboardLayout from "../../../components/dashboard-layout/DashboardLayout";
import styles from "../../../styles/__labEarningPayment.module.scss";
import ServiceNameTooltip from "../../../components/service-name-tooltip/ServiceNameTooltip";
import logo from "../../../assets/images/logo.png";
import user from "../../../assets/images/user.png";
import { IoMdSettings } from "react-icons/io";
import { PiChatCircleTextLight } from "react-icons/pi";
import DialogReschedule from "../../../components/dialog-reschedule/DialogReschedule";
import DialogCreateFollowUp from "../../../components/dialog-create/DialogCreateFollowUp";
import DialogManageAvailability from "../../../components/dialog-manage-availability/DialogManageAvailability";
import DialogPatientRegistration from "../../../components/patient-registration/DialogPatientRegistration";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import AppointmentDetails from "../../../components/appointmentDetails/AppointmentDetails";
import Loader from "../../../components/loader/Loader";
import LabDashboardLayout from "../../../components/labmod-dashboard-layout/LabModDashboardLayout";
import { FiDownload } from "react-icons/fi";
import axios from "axios";
import { APP_PATH } from "../../../api/backendPath";
import { toast } from "../../../components/toast";
import { fetchProviderSettlementsReducer } from "../../../reducer/settlement/fetchProviderSettlementsReducer";

// The "Initiate Withdrawal" button is hidden for centers for now. Set to true
// to show it again (the withdraw page and its route are unchanged).
const SHOW_INITIATE_WITHDRAWAL = false;

// Zeroed financial metrics: the initial state, and what the cards fall back to when a fetch
// fails (so a previous filter's figures are never shown under a new one).
const EMPTY_FINANCIAL_METRICS = {
  totalEarnings: 0,
  codCashOnDelivery: 0,
  onlinePayments: 0,
  adminFees: 0,
  commission: 0,
  netBalance: 0,
  withdrawals: 0,
  completedPayIns: 0,
  transportationFees: 0,
  // Insurance coverage (insured view only) and the loyalty / promotion discount — both
  // are terms of Total Earnings, so they need defaults here or the cards read undefined.
  insuranceCoverage: 0,
  loyaltyPromotion: 0,
};

const LabEarningPayment = ({ onLogout, onNavigateToDashboard }) => {
  const { t, i18n } = useTranslation();
  const [showDetails, setShowDetails] = useState(false);
  const [activeTab, setActiveTab] = useState("Earnings");
  const [withdrawalsPage, setWithdrawalsPage] = useState(1);
  // Withdrawals tab filters
  const [withdrawalSearch, setWithdrawalSearch] = useState("");
  const [withdrawalStatusFilter, setWithdrawalStatusFilter] = useState("");
  const [withdrawalMethodFilter, setWithdrawalMethodFilter] = useState("");
  const [withdrawalDate, setWithdrawalDate] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [dateFilter, setDateFilter] = useState("");
  const itemsPerPage = 10;
  const [date, setDate] = useState(null);

  // Earnings API state
  const [earningsSummary, setEarningsSummary] = useState({
    totalRevenueToday: 0,
    cashOnDeliveryToday: 0,
    onlinePaymentsToday: 0,
    totalEarningAllTime: 0,
  });
  const [earningsData, setEarningsData] = useState([]);
  const [loadingEarnings, setLoadingEarnings] = useState(false);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [totalEarnings, setTotalEarnings] = useState(0);

  // Financial metrics (Total Earnings, COD, Online Payments, Admin Fees, Commission,
  // Net Balance, Withdrawals, Transportation Fees) — same source/insurance toggle as the dashboard.
  const [financialMetrics, setFinancialMetrics] = useState(
    EMPTY_FINANCIAL_METRICS,
  );
  const [loadingMetrics, setLoadingMetrics] = useState(false);
  // Sequence number of the latest metrics request. Picking From then To (or flipping the
  // insurance toggle) fires several requests; only the newest may update the cards, so a
  // slow earlier response can't overwrite the figures for the filter currently selected.
  const metricsRequestSeq = useRef(0);
  // Insurance metrics toggle: OFF = Non-insured metrics (default), ON = Insured.
  const [insuranceOn, setInsuranceOn] = useState(false);
  // Financial metrics date-range filter (From / To). Scopes all metric values to the period.
  const [metricsDateFrom, setMetricsDateFrom] = useState(null);
  const [metricsDateTo, setMetricsDateTo] = useState(null);

  // Filter states
  const [testNameFilter, setTestNameFilter] = useState("");
  const [testTypeFilter, setTestTypeFilter] = useState("");
  const [locationFilter, setLocationFilter] = useState("");
  const [paymentStatusFilter, setPaymentStatusFilter] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const [openDialogReschedule, setOpenDialogReschedule] = useState(false);
  const [openDialogCreateFollowUp, setOpenDialogCreateFollowUp] =
    useState(false);
  const [openDialogManageAvailability, setOpenDialogManageAvailability] =
    useState(false);
  const [openDialogPatientRegistration, setOpenDialogPatientRegistration] =
    useState(false);
  const dispatch = useDispatch();
  const {
    data: apiAppointments,
    isLoading: loading,
    error,
    totalPages: reduxTotalPages,
  } = useSelector((state) => state.fetchDoctorAppointments);

  // Withdrawal (settlement) history for the Withdrawals tab.
  const settlementRecords =
    useSelector((state) => state.payoutAccount?.settlements) || [];
  const loadingWithdrawals = useSelector(
    (state) => state.payoutAccount?.isLoadingSettlements,
  );
  const withdrawalsPagination = useSelector(
    (state) => state.payoutAccount?.settlementsPagination,
  ) || { total: 0, page: 1, limit: 10, total_pages: 1 };

  // Debounce the withdrawals search so we don't hit the endpoint on every keystroke.
  const [debouncedWithdrawalSearch, setDebouncedWithdrawalSearch] =
    useState("");
  useEffect(() => {
    const tid = setTimeout(
      () => setDebouncedWithdrawalSearch(withdrawalSearch),
      500,
    );
    return () => clearTimeout(tid);
  }, [withdrawalSearch]);

  // Reset to the first page whenever a withdrawal filter changes.
  useEffect(() => {
    setWithdrawalsPage(1);
  }, [
    debouncedWithdrawalSearch,
    withdrawalStatusFilter,
    withdrawalMethodFilter,
    withdrawalDate,
  ]);

  // Build the query params for the withdrawals endpoint from the current filters + page.
  const buildWithdrawalParams = () => {
    const params = {
      provider_type: "lab",
      page: withdrawalsPage,
      limit: itemsPerPage,
    };
    if (debouncedWithdrawalSearch.trim())
      params.search = debouncedWithdrawalSearch.trim();
    if (withdrawalStatusFilter && withdrawalStatusFilter !== "ALL")
      params.status = withdrawalStatusFilter;
    if (withdrawalMethodFilter && withdrawalMethodFilter !== "ALL")
      params.payment_method = withdrawalMethodFilter;
    if (withdrawalDate) {
      params.date_from = format(withdrawalDate, "yyyy-MM-dd'T'00:00:00");
      params.date_to = format(withdrawalDate, "yyyy-MM-dd'T'23:59:59");
    }
    return params;
  };

  // Fetch the lab's withdrawal/settlement history (server-side filtered + paginated) when the
  // Withdrawals tab is active or any filter/page changes, and refresh on the socket event.
  useEffect(() => {
    if (activeTab !== "Withdrawals") return;
    const params = buildWithdrawalParams();
    dispatch(fetchProviderSettlementsReducer(params));
    const onSettlementUpdate = () =>
      dispatch(fetchProviderSettlementsReducer(params));
    window.addEventListener("settlement_updated", onSettlementUpdate);
    return () =>
      window.removeEventListener("settlement_updated", onSettlementUpdate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeTab,
    withdrawalsPage,
    debouncedWithdrawalSearch,
    withdrawalStatusFilter,
    withdrawalMethodFilter,
    withdrawalDate,
    dispatch,
  ]);

  // Maps a settlement status to the display label + style class already supported by the table.
  const withdrawalStatusLabel = (status) => {
    switch ((status || "").toLowerCase()) {
      case "pending":
        return t("lab.earningPayment.withdrawals.statusRequested", "Requested");
      case "approved":
        return t(
          "lab.earningPayment.withdrawals.statusProcessing",
          "Processing",
        );
      case "completed":
        return t("lab.earningPayment.withdrawals.statusCompleted", "Completed");
      case "rejected":
        return t("lab.earningPayment.withdrawals.statusFailed", "Failed");
      case "cancelled":
        return t("lab.earningPayment.withdrawals.statusCancelled", "Cancelled");
      default:
        return status || "";
    }
  };

  // Maps a settlement status to one of the existing db-status colour classes.
  const withdrawalStatusClass = (status) => {
    switch ((status || "").toLowerCase()) {
      case "pending":
        return "awaiting-payment"; // orange
      case "approved":
        return "in-progress"; // purple/blue
      case "completed":
        return "payment-complete"; // green
      default:
        return "default";
    }
  };

  // Builds a readable masked bank/account label from the stored account_details.
  const withdrawalAccountLabel = (acc) => {
    if (!acc || typeof acc !== "object") return "—";
    const raw =
      acc.card_number ||
      acc.account_number ||
      acc.billing_account ||
      acc.iban ||
      "";
    const last4 = String(raw).replace(/\s+/g, "").slice(-4);
    if (last4) return `**** ${last4}`;
    return acc.holder_name || acc.bank_name || "—";
  };

  const formatWithdrawAmount = (val) =>
    Number(val || 0).toLocaleString("en-US");

  const clearWithdrawalFilters = () => {
    setWithdrawalSearch("");
    setWithdrawalStatusFilter("");
    setWithdrawalMethodFilter("");
    setWithdrawalDate(null);
    setWithdrawalsPage(1);
  };

  // Server-side pagination meta for the withdrawals table.
  const withdrawalsTotal = withdrawalsPagination.total || 0;
  const withdrawalsTotalPages = withdrawalsPagination.total_pages || 1;

  // Use Redux state for totalPages, fallback to local state if needed
  const today = new Date();
  const formatted = format(today, "MMMM dd, yyyy");
  const [selectedAppointment, setSelectedAppointment] = useState(null);

  // API Functions
  const fetchEarningsSummary = async (selectedDate = null) => {
    setLoadingSummary(true);
    try {
      const token = sessionStorage.getItem("usr_1a2b3c");
      const axiosInstance = axios.create({
        baseURL: process.env.REACT_APP_API_URL,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const dateParam = selectedDate
        ? format(selectedDate, "yyyy-MM-dd")
        : format(today, "yyyy-MM-dd");
      const response = await axiosInstance.get(
        `${APP_PATH.labEarningsSummary}?date=${dateParam}`,
      );

      if (response.data.status) {
        setEarningsSummary(response.data.data);
      } else {
        toast.error(
          response.data.message || "Failed to fetch earnings summary",
        );
      }
    } catch (error) {
      console.error("Error fetching earnings summary:", error);
      toast.error("Failed to fetch earnings summary");
    } finally {
      setLoadingSummary(false);
    }
  };

  // Fetch the financial metrics for the selected order type (non-insured / insured),
  // scoped to the optional From/To date range.
  const fetchFinancialMetrics = async (
    insured = insuranceOn,
    fromDate = metricsDateFrom,
    toDate = metricsDateTo,
  ) => {
    const requestSeq = ++metricsRequestSeq.current;
    const isLatest = () => requestSeq === metricsRequestSeq.current;
    setLoadingMetrics(true);
    try {
      const token = sessionStorage.getItem("usr_1a2b3c");
      const axiosInstance = axios.create({
        baseURL: process.env.REACT_APP_API_URL,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const params = new URLSearchParams({
        order_type: insured ? "insured" : "normal",
      });
      if (fromDate) params.append("date_from", format(fromDate, "yyyy-MM-dd"));
      if (toDate) params.append("date_to", format(toDate, "yyyy-MM-dd"));

      const response = await axiosInstance.get(
        `${APP_PATH.labOrdersMetrics}?${params.toString()}`,
      );

      if (!isLatest()) return;
      if (response.data.status) {
        setFinancialMetrics(response.data.data);
      } else {
        // Don't leave the previous filter's figures on screen as if they matched this one.
        setFinancialMetrics(EMPTY_FINANCIAL_METRICS);
        toast.error(
          response.data.message || "Failed to fetch financial metrics",
        );
      }
    } catch (error) {
      console.error("Error fetching financial metrics:", error);
      if (!isLatest()) return;
      setFinancialMetrics(EMPTY_FINANCIAL_METRICS);
      toast.error("Failed to fetch financial metrics");
    } finally {
      if (isLatest()) setLoadingMetrics(false);
    }
  };

  const fetchEarningsData = async (page = 1) => {
    setLoadingEarnings(true);
    try {
      const token = sessionStorage.getItem("usr_1a2b3c");
      const axiosInstance = axios.create({
        baseURL: process.env.REACT_APP_API_URL,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      // Build query parameters
      const params = new URLSearchParams({
        page: page.toString(),
        limit: itemsPerPage.toString(),
      });

      if (testNameFilter && testNameFilter !== "ALL") {
        params.append("testName", testNameFilter);
      }
      if (locationFilter && locationFilter !== "ALL") {
        params.append("location", locationFilter);
      }
      if (paymentStatusFilter && paymentStatusFilter !== "ALL") {
        params.append("paymentStatus", paymentStatusFilter);
      }
      if (startDate) {
        params.append("startDate", format(startDate, "yyyy-MM-dd"));
      }
      if (endDate) {
        params.append("endDate", format(endDate, "yyyy-MM-dd"));
      }
      if (searchTerm) {
        params.append("search", searchTerm);
      }

      const response = await axiosInstance.get(
        `${APP_PATH.labEarnings}?${params.toString()}`,
      );

      if (response.data.status) {
        setEarningsData(response.data.data.earnings);
        setTotalPages(response.data.data.pagination.totalPages);
        setTotalEarnings(response.data.data.pagination.total);
      } else {
        toast.error(response.data.message || "Failed to fetch earnings data");
      }
    } catch (error) {
      console.error("Error fetching earnings data:", error);
      toast.error("Failed to fetch earnings data");
    } finally {
      setLoadingEarnings(false);
    }
  };

  // Clear filters function
  const clearFilters = () => {
    setTestNameFilter("");
    setTestTypeFilter("");
    setLocationFilter("");
    setPaymentStatusFilter("");
    setStartDate(null);
    setEndDate(null);
    setSearchTerm("");
    setCurrentPage(1);
  };

  // Apply filters function
  const applyFilters = () => {
    setCurrentPage(1);
    fetchEarningsData(1);
  };

  // useEffect to fetch data on component mount
  useEffect(() => {
    fetchEarningsSummary();
    fetchEarningsData();
  }, []);

  // Re-fetch the financial metrics when the insurance toggle flips or the date range changes.
  useEffect(() => {
    fetchFinancialMetrics(insuranceOn, metricsDateFrom, metricsDateTo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [insuranceOn, metricsDateFrom, metricsDateTo]);

  // useEffect to refetch summary when date changes
  useEffect(() => {
    if (date) {
      fetchEarningsSummary(date);
    }
  }, [date]);
  // Helper functions for formatting
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return format(date, "dd-MM-yyyy");
  };

  const formatTime = (timeString) => {
    if (!timeString) return "-";
    try {
      const [hours, minutes] = timeString.split(":");
      const hour24 = parseInt(hours, 10);
      const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
      const period = hour24 >= 12 ? "PM" : "AM";
      return `${hour12}:${minutes} ${period}`;
    } catch (error) {
      return timeString;
    }
  };

  const formatLocation = (location) => {
    if (location === "HOME_VISIT") return "Home Visit";
    if (location === "ON_SITE") return "On-Site";
    return location || "-";
  };

  const formatPaymentMethod = (method) => {
    switch (method) {
      case "COD":
        return "Cash on Delivery";
      case "WALLET":
        return "Wallet";
      case "CARD_AREEBA":
        return "Card";
      case "INSURANCE":
        return "Insurance";
      default:
        return method || "-";
    }
  };

  // Display a status label without underscores (e.g. "ORDER_ACCEPTED" → "ORDER ACCEPTED").
  const formatStatusLabel = (status) =>
    status ? String(status).replace(/_/g, " ") : status;

  const getStatusColor = (status) => {
    // Normalize underscores to spaces so backend values like "ORDER_ACCEPTED" /
    // "IN_PROGRESS" match the space-separated cases below and get a colored badge.
    const normalizedStatus = status?.toLowerCase().trim().replace(/_/g, " ");

    switch (normalizedStatus) {
      // Order Status Colors (matching your screenshot)
      case "request submitted":
      case "awaiting confirmation":
        return "awaiting-confirmation"; // Slate/gray-blue
      case "invoice received":
      case "awaiting payment":
      case "pending":
        return "awaiting-payment"; // Orange color
      case "order accepted":
      case "accepted":
        return "order-accepted"; // Indigo color
      case "technician assigned":
        return "technician-assigned"; // Sky blue
      case "technician dispatched":
        return "technician-dispatched"; // Cyan
      case "sample collected":
        return "sample-collected"; // Teal
      case "in-progress":
      case "in progress":
      case "ongoing":
        return "in-progress"; // Purple color
      case "completed":
      case "complete":
        return "completed"; // Green color
      case "rejected":
        return "rejected"; // Red
      case "cancelled":
      case "canceled":
        return "cancelled"; // Gray

      // Payment Status Colors (matching your screenshot)
      case "cod":
      case "cash on delivery":
        return "cod"; // Yellow/Orange color
      case "payment complete":
      case "payment completed":
      case "paid":
        return "payment-complete"; // Green color
      case "failed":
        return "rejected"; // Red — keep Failed consistent with the order management page

      // Test Type Colors
      case "lab":
        return "lab-type"; // Blue color
      case "imaging":
        return "imaging-type"; // Purple color

      // Default fallback
      default:
        return "default";
    }
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages && !loadingEarnings) {
      setCurrentPage(newPage);
      fetchEarningsData(newPage);
    }
  };

  // Bind appointments from API (normalize shape for the existing UI)
  const appointments = (apiAppointments || []).map((a) => {
    const patient = a?.patient || {};
    const name = `${patient?.full_name || ""} ${
      patient?.surname || ""
    }`.trim();
    const avatar = patient?.avatar_presigned_url || patient?.avatar || user;
    // Determine typeIcon based on consultation_type
    const typeIcon =
      a.consultation_type === "video"
        ? "video"
        : a.consultation_type === "audio"
          ? "mic"
          : "message";
    const type = a.consultation_type === "video" ? "Online Consult" : "On-Site";
    return {
      id: a.id,
      name,
      avatar,
      location: a?.location || a?.onsite_details || "",
      date: a?.appointment_date || a?.booking_date || "",
      time:
        a?.formatted_time ||
        (a?.appointment_time_slot
          ? a.appointment_time_slot.charAt(0).toUpperCase() +
            a.appointment_time_slot.slice(1)
          : ""),
      typeIcon,
      type,
      status: a?.status || "",
      raw: a,
    };
  });

  // Sort appointments (filtering is done server-side)
  const sortedAppointments = useMemo(() => {
    // Sort appointments
    const sorted = [...appointments].sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [appointments, sortField, sortDirection]);

  // Pagination
  // const totalPages = Math.ceil(
  //   filteredAndSortedAppointments.length / itemsPerPage
  // );
  // const startIndex = (currentPage - 1) * itemsPerPage;
  // const paginatedAppointments = filteredAndSortedAppointments.slice(
  //   startIndex,
  //   startIndex + itemsPerPage
  // );

  const paginatedAppointments = sortedAppointments;

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const handleNavigate = (page) => {
    if (onNavigateToDashboard) {
      onNavigateToDashboard(page);
    }
  };

  // Normalize status value for API (convert display format to API format)
  const normalizeStatusForAPI = (status) => {
    if (!status || status === "All") return null;
    // Convert display format to API format
    const statusMap = {
      Pending: "pending",
      Upcoming: "upcoming",
      Ongoing: "ongoing",
      Completed: "completed",
      Cancelled: "cancelled",
      "Reschedule Requested": "reschedule_requested",
      reschedule_requested: "reschedule_requested",
      pending: "pending",
      upcoming: "upcoming",
      ongoing: "ongoing",
      completed: "completed",
      cancelled: "cancelled",
    };
    return statusMap[status] || status.toLowerCase().replace(/\s+/g, "_");
  };

  // Function to fetch appointments with current filters
  // Optional parameters allow overriding filter values (useful for tab clicks and reset)
  // Use undefined as default to distinguish between "not provided" and "explicitly null"
  const fetchAppointmentsWithFilters = (
    pageNum = currentPage,
    statusOverride = undefined,
    dateOverride = undefined,
    searchOverride = undefined,
  ) => {
    // Use override values if provided (including null), otherwise use current state
    // undefined means "use state value", null means "explicitly clear/no filter"
    const dateToUse = dateOverride !== undefined ? dateOverride : date;
    const statusToUse =
      statusOverride !== undefined ? statusOverride : statusFilter;
    const searchToUse =
      searchOverride !== undefined ? searchOverride : searchTerm;

    const formattedDate = formatDate(dateToUse);
    const normalizedStatus = normalizeStatusForAPI(statusToUse);

    // Build params object - only include defined values
    const params = {
      page: pageNum,
      limit: itemsPerPage,
    };

    // Only add date if it's a valid formatted date string (not null/undefined/empty)
    if (
      formattedDate &&
      formattedDate !== null &&
      formattedDate !== "null" &&
      formattedDate !== ""
    ) {
      params.date = formattedDate;
    }

    if (searchToUse && searchToUse.trim()) {
      params.search = searchToUse.trim();
    }

    if (normalizedStatus) {
      params.status = normalizedStatus;
    }
  };

  // Format currency function
  const formatCurrency = (amount) => {
    // IQD has no minor unit — always show whole numbers, never decimals.
    return `IQD ${Math.round(Number(amount) || 0).toLocaleString("en-US")}`;
  };

  const handleReschedules = (data) => {
    setOpenDialogReschedule(true);
    setSelectedAppointment(data);
  };

  const CreateFollowUp = (data) => {
    setOpenDialogCreateFollowUp(true);
    setSelectedAppointment(data);
  };

  const CreditCard = () => (
    <svg
      width="32"
      height="32"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M21.5 6.30775V17.6923C21.5 18.1974 21.325 18.625 20.975 18.975C20.625 19.325 20.1974 19.5 19.6923 19.5H4.30775C3.80258 19.5 3.375 19.325 3.025 18.975C2.675 18.625 2.5 18.1974 2.5 17.6923V6.30775C2.5 5.80258 2.675 5.375 3.025 5.025C3.375 4.675 3.80258 4.5 4.30775 4.5H19.6923C20.1974 4.5 20.625 4.675 20.975 5.025C21.325 5.375 21.5 5.80258 21.5 6.30775ZM4 8.404H20V6.30775C20 6.23075 19.9679 6.16025 19.9038 6.09625C19.8398 6.03208 19.7693 6 19.6923 6H4.30775C4.23075 6 4.16025 6.03208 4.09625 6.09625C4.03208 6.16025 4 6.23075 4 6.30775V8.404ZM4 11.596V17.6923C4 17.7692 4.03208 17.8398 4.09625 17.9038C4.16025 17.9679 4.23075 18 4.30775 18H19.6923C19.7693 18 19.8398 17.9679 19.9038 17.9038C19.9679 17.8398 20 17.7692 20 17.6923V11.596H4Z"
        fill="#2D2D37"
      />
    </svg>
  );

  const CodIcon = () => (
    <svg
      width="40"
      height="40"
      viewBox="0 0 40 40"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M22.9173 21.1847C21.7529 21.1847 20.7673 20.7814 19.9607 19.9747C19.154 19.1681 18.7507 18.1825 18.7507 17.0181C18.7507 15.8536 19.154 14.8681 19.9607 14.0614C20.7673 13.2547 21.7529 12.8514 22.9173 12.8514C24.0818 12.8514 25.0673 13.2547 25.874 14.0614C26.6807 14.8681 27.084 15.8536 27.084 17.0181C27.084 18.1825 26.6807 19.1681 25.874 19.9747C25.0673 20.7814 24.0818 21.1847 22.9173 21.1847ZM11.9236 26.3452C11.0953 26.3452 10.3861 26.0502 9.79609 25.4602C9.20609 24.8702 8.91109 24.1609 8.91109 23.3322V10.7039C8.91109 9.87557 9.20609 9.16641 9.79609 8.57641C10.3861 7.98641 11.0953 7.69141 11.9236 7.69141H33.9107C34.7393 7.69141 35.4486 7.98641 36.0386 8.57641C36.6286 9.16641 36.9236 9.87557 36.9236 10.7039V23.3322C36.9236 24.1609 36.6286 24.8702 36.0386 25.4602C35.4486 26.0502 34.7393 26.3452 33.9107 26.3452H11.9236ZM14.4236 23.8452H31.4111C31.4111 23.014 31.7061 22.304 32.2961 21.7152C32.8861 21.1265 33.5953 20.8322 34.4236 20.8322V13.2039C33.5925 13.2039 32.8826 12.9089 32.294 12.3189C31.7054 11.7289 31.4111 11.0197 31.4111 10.1914H14.4236C14.4236 11.0225 14.1286 11.7324 13.5386 12.321C12.9486 12.9096 12.2394 13.2039 11.4111 13.2039V20.8322C12.2422 20.8322 12.9521 21.1272 13.5407 21.7172C14.1293 22.3072 14.4236 23.0165 14.4236 23.8452ZM30.7057 32.1785H6.09068C5.26206 32.1785 4.55276 31.8835 3.96276 31.2935C3.37276 30.7035 3.07776 29.9942 3.07776 29.1656V13.9093C3.07776 13.5552 3.19762 13.2582 3.43734 13.0185C3.67706 12.779 3.97401 12.6593 4.32818 12.6593C4.68262 12.6593 4.97943 12.779 5.21859 13.0185C5.45804 13.2582 5.57776 13.5552 5.57776 13.9093V29.1656C5.57776 29.2936 5.63109 29.4111 5.73776 29.5181C5.8447 29.625 5.96234 29.6785 6.09068 29.6785H30.7057C31.0598 29.6785 31.3568 29.7984 31.5965 30.0381C31.836 30.2775 31.9557 30.5745 31.9557 30.9289C31.9557 31.2834 31.836 31.5802 31.5965 31.8193C31.3568 32.0588 31.0598 32.1785 30.7057 32.1785ZM11.9236 23.8452H11.4111V10.1914H11.9236C11.7847 10.1914 11.6646 10.2421 11.5632 10.3435C11.4618 10.4449 11.4111 10.565 11.4111 10.7039V23.3322C11.4111 23.4711 11.4618 23.5913 11.5632 23.6927C11.6646 23.7943 11.7847 23.8452 11.9236 23.8452Z"
        fill="#2D2D37"
      />
    </svg>
  );

  const TotalEarningIcon = () => (
    <svg
      width="40"
      height="40"
      viewBox="0 0 40 40"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M9.58376 27.0838V17.0838C9.58376 16.7296 9.70362 16.4327 9.94335 16.193C10.1831 15.9535 10.48 15.8338 10.8342 15.8338C11.1886 15.8338 11.4856 15.9535 11.725 16.193C11.9642 16.4327 12.0838 16.7296 12.0838 17.0838V27.0838C12.0838 27.438 11.9639 27.7349 11.7242 27.9746C11.4845 28.2141 11.1875 28.3338 10.8333 28.3338C10.4789 28.3338 10.1821 28.2141 9.94293 27.9746C9.70348 27.7349 9.58376 27.438 9.58376 27.0838ZM18.7504 27.0838V17.0838C18.7504 16.7296 18.8703 16.4327 19.11 16.193C19.3497 15.9535 19.6467 15.8338 20.0008 15.8338C20.3553 15.8338 20.6521 15.9535 20.8913 16.193C21.1307 16.4327 21.2504 16.7296 21.2504 17.0838V27.0838C21.2504 27.438 21.1306 27.7349 20.8908 27.9746C20.6511 28.2141 20.3542 28.3338 20 28.3338C19.6456 28.3338 19.3488 28.2141 19.1096 27.9746C18.8702 27.7349 18.7504 27.438 18.7504 27.0838ZM5.86585 34.1671C5.51168 34.1671 5.21487 34.0473 4.97543 33.8075C4.73571 33.5678 4.61584 33.2709 4.61584 32.9167C4.61584 32.5623 4.73571 32.2655 4.97543 32.0263C5.21487 31.7869 5.51168 31.6671 5.86585 31.6671H34.135C34.4892 31.6671 34.786 31.787 35.0254 32.0267C35.2652 32.2664 35.385 32.5634 35.385 32.9175C35.385 33.272 35.2652 33.5688 35.0254 33.808C34.786 34.0474 34.4892 34.1671 34.135 34.1671H5.86585ZM27.9171 27.0838V17.0838C27.9171 16.7296 28.037 16.4327 28.2767 16.193C28.5164 15.9535 28.8133 15.8338 29.1675 15.8338C29.522 15.8338 29.8188 15.9535 30.0579 16.193C30.2974 16.4327 30.4171 16.7296 30.4171 17.0838V27.0838C30.4171 27.438 30.2972 27.7349 30.0575 27.9746C29.8178 28.2141 29.5208 28.3338 29.1667 28.3338C28.8122 28.3338 28.5153 28.2141 28.2758 27.9746C28.0367 27.7349 27.9171 27.438 27.9171 27.0838ZM34.1992 12.5005H5.68334C5.38668 12.5005 5.13459 12.3969 4.92709 12.1896C4.71959 11.9827 4.61584 11.7312 4.61584 11.4351V10.7842C4.61584 10.5673 4.67015 10.3802 4.77876 10.223C4.88765 10.066 5.03098 9.93839 5.20876 9.84005L18.6767 3.25047C19.0928 3.04963 19.5333 2.94922 19.9983 2.94922C20.4633 2.94922 20.9053 3.04963 21.3242 3.25047L34.7246 9.79839C34.934 9.90533 35.0964 10.0549 35.2117 10.2471C35.3272 10.4396 35.385 10.6463 35.385 10.8671V11.3188C35.385 11.6599 35.2733 11.9421 35.05 12.1655C34.8267 12.3888 34.5431 12.5005 34.1992 12.5005ZM10.5258 10.0005H29.475L20.225 5.51338C20.15 5.48116 20.0752 5.46505 20.0004 5.46505C19.9257 5.46505 19.8508 5.48116 19.7758 5.51338L10.5258 10.0005Z"
        fill="#1F1F1F"
      />
    </svg>
  );

  // Raw Net Balance drives the Pay In / Pay Out cards (same logic as the pharmacy dashboard):
  //   • positive → Tiryaq owes the center → Pay In (payment for the center) → green
  //   • negative → the center owes Tiryaq → Pay Out (payment for Tiryaq)     → red
  const netBalanceRaw = Number(financialMetrics.netBalance || 0);
  // Pay In / Pay Out cards show ACTUAL completed settlement amounts (same as the
  // doctor dashboard): Pay In = completed Pay-Out settlements (Tiryaq → center) held
  // in `withdrawals`; Pay Out = completed Pay-In settlements (center → Tiryaq).
  const withdrawalsRaw = Number(financialMetrics.withdrawals || 0);
  const completedPayInsRaw = Number(financialMetrics.completedPayIns || 0);

  const dashboardStats = [
    {
      id: 1,
      icon: TotalEarningIcon,
      label: t("lab.earningPayment.stats.totalEarnings", "Total Earnings"),
      value: loadingMetrics
        ? t("lab.earningPayment.loading")
        : formatCurrency(financialMetrics.totalEarnings),
      iconBg: "#FAF5FF",
    },
    {
      id: 2,
      icon: CodIcon,
      label: t("lab.earningPayment.stats.cod", "COD (Cash on Delivery)"),
      value: loadingMetrics
        ? t("lab.earningPayment.loading")
        : formatCurrency(financialMetrics.codCashOnDelivery),
      iconBg: "#FFFBEB",
    },
    {
      id: 3,
      icon: CreditCard,
      label: t("lab.earningPayment.stats.onlinePayments", "Online Payments"),
      value: loadingMetrics
        ? t("lab.earningPayment.loading")
        : formatCurrency(financialMetrics.onlinePayments),
      iconBg: "#ECFDF5",
    },
    {
      id: 4,
      icon: CreditCard,
      label: t("lab.earningPayment.stats.adminFees", "Admin Fees"),
      value: loadingMetrics
        ? t("lab.earningPayment.loading")
        : formatCurrency(financialMetrics.adminFees),
      iconBg: "#EFF6FF",
    },
    {
      id: 5,
      icon: CreditCard,
      label: t("lab.earningPayment.stats.commission", "Commission"),
      value: loadingMetrics
        ? t("lab.earningPayment.loading")
        : formatCurrency(financialMetrics.commission),
      iconBg: "#FAF5FF",
    },
    // Insurance coverage card — only on the insured view (insurance toggle ON).
    ...(insuranceOn
      ? [
          {
            id: 11,
            icon: CreditCard,
            label: t(
              "lab.earningPayment.stats.insuranceCoverage",
              "Total Amount Covered by Insurance",
            ),
            value: loadingMetrics
              ? t("lab.earningPayment.loading")
              : formatCurrency(financialMetrics.insuranceCoverage),
            iconBg: "#EFF6FF",
          },
        ]
      : []),
    {
      id: 6,
      icon: TotalEarningIcon,
      label: t("lab.earningPayment.stats.netBalance", "Net Balance"),
      value: loadingMetrics
        ? t("lab.earningPayment.loading")
        : formatCurrency(financialMetrics.netBalance),
      iconBg: "#ECFDF5",
      color:
        netBalanceRaw > 0
          ? "#16a34a"
          : netBalanceRaw < 0
            ? "#dc2626"
            : undefined,
    },
    {
      id: 9,
      icon: TotalEarningIcon,
      label: t(
        "lab.earningPayment.stats.payInCard",
        "Pay In (Payment for the Center)",
      ),
      value: loadingMetrics
        ? t("lab.earningPayment.loading")
        : formatCurrency(withdrawalsRaw),
      iconBg: "#ECFDF5",
      color: withdrawalsRaw > 0 ? "#16a34a" : undefined,
    },
    {
      id: 10,
      icon: CreditCard,
      label: t(
        "lab.earningPayment.stats.payOutCard",
        "Pay Out (Payment for Tiryaq)",
      ),
      value: loadingMetrics
        ? t("lab.earningPayment.loading")
        : formatCurrency(completedPayInsRaw),
      iconBg: "#ECFDF5",
      color: completedPayInsRaw > 0 ? "#16a34a" : undefined,
    },
    // Withdrawals card removed: a withdrawal request is represented by the
    // Pay In (Payment for the Center) amount, so a separate Withdrawals metric
    // card is redundant on the Metrics section.
    {
      id: 8,
      icon: CreditCard,
      label: t("lab.earningPayment.stats.transportationFees", "Transportation Fees"),
      value: loadingMetrics
        ? t("lab.earningPayment.loading")
        : formatCurrency(financialMetrics.transportationFees),
      iconBg: "#EFF6FF",
    },
    {
      id: 12,
      icon: TotalEarningIcon,
      label: t(
        "lab.earningPayment.stats.loyaltyPromotion",
        "Loyalty Point & Marketing Promotion",
      ),
      // Loyalty / promotion discount patients redeemed on this lab's bookings, reimbursed
      // to the lab by the platform and therefore part of Total Earnings:
      //   Normal  : Total Earnings = COD + Online + Loyalty & Promotion
      //   Insured : Total Earnings = COD + Online + Insurance Coverage
      //                            + Loyalty & Promotion
      // Shown on BOTH views. This page reads the SAME metrics endpoint as the dashboard, so
      // the card must exist here too — otherwise Total Earnings would include an amount
      // with no line item on screen to account for it.
      value: loadingMetrics
        ? t("lab.earningPayment.loading")
        : formatCurrency(financialMetrics.loyaltyPromotion),
      iconBg: "#FAF5FF",
    },
  ];

  const labOrdersData = [
    {
      id: "LT-0001",
      name: "Mohamed Hassan",
      serviceDate: "20-12-2025",
      serviceTime: "Morning, 10:00 AM",
      testName: "Complete Blood Count",
      testType: "Lab",
      paymentMethod: "",
      orderStatus: "Awaiting Payment",
      paymentStatus: "",
    },
    {
      id: "LT-0002",
      name: "Lyla Khalid",
      serviceDate: "20-12-2025",
      serviceTime: "Morning, 10:00 AM",
      testName: "Vitamin D",
      testType: "Lab",
      paymentMethod: "",
      orderStatus: "Awaiting Payment",
      paymentStatus: "",
    },
    {
      id: "LT-0003",
      name: "Kamla Khalid",
      serviceDate: "20-12-2025",
      serviceTime: "Morning, 10:00 AM",
      testName: "Urinalysis",
      testType: "Lab",
      paymentMethod: "",
      orderStatus: "Awaiting Payment",
      paymentStatus: "",
    },
    {
      id: "LT-0004",
      name: "Ruby",
      serviceDate: "20-12-2025",
      serviceTime: "Morning, 10:00 AM",
      testName: "Complete Blood Count",
      testType: "Lab",
      paymentMethod: "Cash on Delivery",
      orderStatus: "Order Accepted",
      paymentStatus: "COD",
    },
    {
      id: "LT-0005",
      name: "Kyle",
      serviceDate: "20-02-2025",
      serviceTime: "Morning, 10:00 AM",
      testName: "MRI Brain",
      testType: "Imaging",
      paymentMethod: "",
      orderStatus: "Awaiting Payment",
      paymentStatus: "",
    },
    {
      id: "LT-0006",
      name: "Olivia",
      serviceDate: "20-02-2025",
      serviceTime: "Morning, 10:00 AM",
      testName: "Urinalysis",
      testType: "Lab",
      paymentMethod: "Wallet + Insurance",
      orderStatus: "Order Accepted",
      paymentStatus: "Payment Complete",
    },
    {
      id: "LT-0007",
      name: "Ruddy",
      serviceDate: "10-02-2025",
      serviceTime: "Morning, 10:00 AM",
      testName: "Calcium",
      testType: "Lab",
      paymentMethod: "Cash on Delivery",
      orderStatus: "Order Accepted",
      paymentStatus: "COD",
    },
    {
      id: "LT-0008",
      name: "Kevin",
      serviceDate: "20-02-2025",
      serviceTime: "Morning, 10:00 AM",
      testName: "X-Ray Knee",
      testType: "Imaging",
      paymentMethod: "Online",
      orderStatus: "Order Accepted",
      paymentStatus: "Payment Complete",
    },
    {
      id: "LT-0009",
      name: "Rishabh",
      serviceDate: "20-02-2025",
      serviceTime: "Morning, 10:00 AM",
      testName: "CT Scan Brain",
      testType: "Imaging",
      paymentMethod: "Wallet",
      orderStatus: "In-Progress",
      paymentStatus: "Payment Complete",
    },
    {
      id: "LT-0010",
      name: "Rohan",
      serviceDate: "10-02-2025",
      serviceTime: "Morning, 10:00 AM",
      testName: "X-Ray Chest",
      testType: "Imaging",
      paymentMethod: "Insurance",
      orderStatus: "Completed",
      paymentStatus: "Payment Complete",
    },
  ];

  return (
    <LabDashboardLayout
      currentPage="lab_earning_payment"
      onNavigate={handleNavigate}
      onLogout={onLogout}
    >
      {/* Front-face service-name tooltip (position:fixed, never clipped by the
          table's horizontal scroll box). */}
      <ServiceNameTooltip styles={styles} />
      <DialogPatientRegistration
        open={openDialogPatientRegistration}
        onClose={() => {
          setOpenDialogPatientRegistration(false);
        }}
        children={undefined}
      />

      {showDetails ? (
        <AppointmentDetails
          handleBack={() => setShowDetails(false)}
          homeVisit={false}
          selectedAppointment={selectedAppointment}
        />
      ) : (
        <>
          <div
            className={styles["apt-page-header"]}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: "12px",
            }}
          >
            <h1>{t("lab.earningPayment.title")}</h1>
            {SHOW_INITIATE_WITHDRAWAL && (
            <Link to="/lab_withdraw_from_wallet">
              <button type="button" className={styles["wallet-withdraw-btn"]}>
                <svg
                  width="21"
                  height="20"
                  viewBox="0 0 21 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <mask
                    id="mask0_lab_withdraw"
                    maskUnits="userSpaceOnUse"
                    x="0"
                    y="0"
                    width="21"
                    height="20"
                  >
                    <path d="M20.2852 0H0.285156V20H20.2852V0Z" fill="white" />
                  </mask>
                  <g mask="url(#mask0_lab_withdraw)">
                    <path
                      d="M6.26064 18.9577C4.27731 18.9577 2.65234 17.516 2.65234 15.741V14.041C2.65234 13.6993 2.93568 13.416 3.27734 13.416C3.61901 13.416 3.90234 13.6993 3.90234 14.041C3.90234 15.0827 4.91064 15.866 6.26064 15.866C7.61064 15.866 8.61899 15.0827 8.61899 14.041C8.61899 13.6993 8.90231 13.416 9.24398 13.416C9.58564 13.416 9.86898 13.6993 9.86898 14.041V15.741C9.86898 17.516 8.25231 18.9577 6.26064 18.9577ZM4.11897 16.5577C4.48564 17.241 5.31064 17.7077 6.26064 17.7077C7.21064 17.7077 8.03565 17.2327 8.40232 16.5577C7.81065 16.916 7.07731 17.1244 6.26064 17.1244C5.44398 17.1244 4.71064 16.916 4.11897 16.5577Z"
                      fill="#1F1F1F"
                    />
                    <path
                      d="M6.26064 14.8328C4.89398 14.8328 3.669 14.2078 3.06067 13.2162C2.794 12.7828 2.65234 12.2745 2.65234 11.7578C2.65234 10.8828 3.03567 10.0662 3.73567 9.45784C5.08567 8.27453 7.41065 8.27457 8.76898 9.44959C9.46898 10.0663 9.86064 10.8828 9.86064 11.7578C9.86064 12.2745 9.71898 12.7828 9.45231 13.2162C8.85231 14.2078 7.62731 14.8328 6.26064 14.8328ZM6.26064 9.79125C5.61064 9.79125 5.01066 10.0078 4.56066 10.3995C4.13566 10.7662 3.90234 11.2495 3.90234 11.7578C3.90234 12.0495 3.97731 12.3163 4.12731 12.5663C4.51064 13.1996 5.32731 13.5913 6.26064 13.5913C7.19398 13.5913 8.01064 13.1996 8.38564 12.5746C8.53564 12.3329 8.61065 12.0579 8.61065 11.7663C8.61065 11.2579 8.37734 10.7745 7.95234 10.3995C7.51067 10.0078 6.91064 9.79125 6.26064 9.79125Z"
                      fill="#2D2D37"
                    />
                    <path
                      d="M6.26064 17.1248C4.20231 17.1248 2.65234 15.7998 2.65234 14.0498V11.7582C2.65234 9.98317 4.26898 8.5415 6.26064 8.5415C7.20231 8.5415 8.10233 8.8665 8.77731 9.44984C9.47731 10.0665 9.86898 10.8832 9.86898 11.7582V14.0498C9.86898 15.7998 8.31898 17.1248 6.26064 17.1248ZM6.26064 9.7915C4.96064 9.7915 3.90234 10.6748 3.90234 11.7582V14.0498C3.90234 15.0915 4.91064 15.8748 6.26064 15.8748C7.61064 15.8748 8.61899 15.0915 8.61899 14.0498V11.7582C8.61899 11.2498 8.38568 10.7665 7.96068 10.3915C7.51068 10.0082 6.91064 9.7915 6.26064 9.7915Z"
                      fill="#2D2D37"
                    />
                    <path
                      d="M16.1544 12.3332C14.896 12.3332 13.8294 11.3998 13.7294 10.1998C13.6627 9.50817 13.9127 8.83317 14.4127 8.3415C14.8294 7.90816 15.421 7.6665 16.046 7.6665H17.7877C18.6127 7.6915 19.246 8.3415 19.246 9.1415V10.8582C19.246 11.6582 18.6127 12.3082 17.8127 12.3332H16.1544ZM17.7627 8.9165H16.0544C15.7627 8.9165 15.496 9.02484 15.3044 9.22484C15.0627 9.45817 14.946 9.77484 14.9794 10.0915C15.021 10.6415 15.5544 11.0832 16.1544 11.0832H17.7877C17.896 11.0832 17.996 10.9832 17.996 10.8582V9.1415C17.996 9.0165 17.896 8.92484 17.7627 8.9165Z"
                      fill="#2D2D37"
                    />
                    <path
                      d="M13.6203 17.7082H11.537C11.1953 17.7082 10.912 17.4248 10.912 17.0832C10.912 16.7415 11.1953 16.4582 11.537 16.4582H13.6203C15.7703 16.4582 17.162 15.0665 17.162 12.9165V12.3332H16.1536C14.8953 12.3332 13.8286 11.3998 13.7286 10.1998C13.662 9.50817 13.912 8.83317 14.412 8.3415C14.8287 7.90818 15.4203 7.66651 16.0453 7.66651H17.1536V7.08317C17.1536 5.13317 16.012 3.79149 14.162 3.57482C13.962 3.54149 13.787 3.5415 13.612 3.5415H6.11198C5.91198 3.5415 5.7203 3.55816 5.52864 3.58316C3.6953 3.8165 2.57031 5.14984 2.57031 7.08317V8.74984C2.57031 9.0915 2.28698 9.37484 1.94531 9.37484C1.60365 9.37484 1.32031 9.0915 1.32031 8.74984V7.08317C1.32031 4.5165 2.90368 2.65818 5.36201 2.34985C5.58701 2.31651 5.84531 2.2915 6.11198 2.2915H13.612C13.812 2.2915 14.0703 2.29984 14.337 2.3415C16.7953 2.62484 18.4036 4.4915 18.4036 7.08317V8.29151C18.4036 8.63317 18.1203 8.9165 17.7786 8.9165H16.0453C15.7536 8.9165 15.487 9.02484 15.2954 9.22484C15.0537 9.45817 14.937 9.77484 14.9703 10.0915C15.012 10.6415 15.5454 11.0832 16.1454 11.0832H17.787C18.1286 11.0832 18.412 11.3665 18.412 11.7082V12.9165C18.412 15.7832 16.487 17.7082 13.6203 17.7082Z"
                      fill="#2D2D37"
                    />
                  </g>
                </svg>
                {t("wallet.balance.withdraw_button", "Initiate Withdrawal")}
              </button>
            </Link>
            )}
          </div>

          {/* Insurance Coverage toggle — OFF = Non-insured metrics, ON = Insured (adds the
              "Total Amount Covered by Insurance" card). Previously hidden with
              display:none; now enabled, matching the pharmacy Account & Payments page. */}
          <div className={styles["InsuranceCoverageToggle"]}>
            <div className={styles["toggle-container"]}>
              <span className={styles["toggle-label"]}>
                {t(
                  "lab.earningPayment.insuranceCoverage",
                  "Insurance Coverage",
                )}
              </span>
              <label className={styles["toggle-switch"]}>
                <input
                  type="checkbox"
                  checked={insuranceOn}
                  onChange={(e) => setInsuranceOn(e.target.checked)}
                />
                <span className={styles["toggle-slider"]}></span>
              </label>
            </div>
          </div>

          {/* Metrics label on the left + From/To date-range picker on the right — same design
              and calendar icon as the pharmacy & insurance dashboards. Scopes all metric cards. */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 12,
              marginBottom: "1rem",
            }}
          >
            <span style={{ fontSize: 16, fontWeight: 500, color: "#099494" }}>
              {t("lab.earningPayment.metrics", "Metrics")}
            </span>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                flexWrap: "wrap",
              }}
            >
              <div className="ins-metrics-datepicker">
                <DatePicker
                  selected={metricsDateFrom}
                  onChange={(d) => setMetricsDateFrom(d)}
                  selectsStart
                  startDate={metricsDateFrom}
                  endDate={metricsDateTo}
                  maxDate={metricsDateTo || undefined}
                  dateFormat="MM/dd/yyyy"
                  placeholderText={t(
                    "lab.earningPayment.filter.fromPlaceholder",
                    "From date",
                  )}
                  isClearable
                />
              </div>
              <div className="ins-metrics-datepicker">
                <DatePicker
                  selected={metricsDateTo}
                  onChange={(d) => setMetricsDateTo(d)}
                  selectsEnd
                  startDate={metricsDateFrom}
                  endDate={metricsDateTo}
                  minDate={metricsDateFrom || undefined}
                  dateFormat="MM/dd/yyyy"
                  placeholderText={t(
                    "lab.earningPayment.filter.toPlaceholder",
                    "To date",
                  )}
                  isClearable
                />
              </div>
              {(metricsDateFrom || metricsDateTo) && (
                <button
                  type="button"
                  onClick={() => {
                    setMetricsDateFrom(null);
                    setMetricsDateTo(null);
                  }}
                  style={{
                    padding: "9px 14px",
                    border: "1px solid #d1d5db",
                    borderRadius: 8,
                    fontSize: 14,
                    background: "#fff",
                    cursor: "pointer",
                    color: "#374151",
                  }}
                >
                  {t("lab.earningPayment.filter.clear", "Clear")}
                </button>
              )}
            </div>
          </div>

          {/* Stats Cards */}
          <div className={styles["db-stats-grid"]}>
            {dashboardStats.map((item) => {
              return (
                <div className={`${styles["db-lab-card"]}`} key={item.id}>
                  <div
                    className={`${styles["db-lab-cardIcon"]}`}
                    style={{ backgroundColor: item.iconBg }}
                  >
                    <item.icon />
                  </div>
                  <div className={`${styles["db-lab-cardText"]}`}>
                    <p>{item.label}</p>
                    <h3 style={item.color ? { color: item.color } : undefined}>
                      {item.value}
                    </h3>
                  </div>
                </div>
              );
            })}
          </div>

          <div className={styles["apt-appointments-section"]}>
            <div
              className={styles["apt-tabButtons-wrapper"]}
              style={{ marginBottom: "1rem" }}
            >
              <button
                type="button"
                className={`${styles["tabButton"]} ${activeTab === "Earnings" ? styles["active"] : ""}`}
                onClick={() => setActiveTab("Earnings")}
              >
                {t("lab.earningPayment.section.earnings")}
              </button>
              <button
                type="button"
                className={`${styles["tabButton"]} ${activeTab === "Withdrawals" ? styles["active"] : ""}`}
                onClick={() => setActiveTab("Withdrawals")}
              >
                {t("lab.earningPayment.section.withdrawals", "Withdrawals")}
              </button>
            </div>

            {activeTab === "Earnings" && (
              <>
                {/* Filters */}
                <div className={styles["apt-filters"]}>
                  <div className={styles["apt-filter-group"]}>
                    <div className={styles["apt-search-wrapper"]}>
                      <Search className={styles["apt-search-icon"]} />
                      <input
                        type="text"
                        placeholder={t(
                          "lab.earningPayment.filters.searchPlaceholder",
                        )}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className={styles["apt-search-input"]}
                      />
                    </div>
                    <div className={styles["date-wrapper"]}>
                      <DatePicker
                        key={`datepicker-${date ? date.getTime() : "empty"}`}
                        className={styles["apt-date-filter"]}
                        selected={date}
                        onChange={(d) => setDate(d)}
                        placeholderText={t(
                          "lab.earningPayment.filters.selectDate",
                        )}
                      />
                    </div>
                    <button
                      className={styles["apt-apply-btn"]}
                      onClick={applyFilters}
                      disabled={loadingEarnings}
                    >
                      {t("lab.earningPayment.filters.applyFilters")}
                    </button>
                    <button
                      className={styles["apt-apply-btn"]}
                      onClick={clearFilters}
                      disabled={loadingEarnings}
                      style={{ marginLeft: "8px", backgroundColor: "#6c757d" }}
                    >
                      {t("lab.earningPayment.filters.clearFilters")}
                    </button>
                  </div>
                </div>

                {/* Appointments Table */}
                <div className={styles["apt-appointments-table"]}>
                  <table>
                    <thead>
                      <tr>
                        <th>{t("lab.earningPayment.table.id")}</th>
                        <th>{t("lab.earningPayment.table.name")}</th>
                        <th>{t("lab.earningPayment.table.serviceDate")}</th>
                        <th>{t("lab.earningPayment.table.serviceTime")}</th>
                        <th>{t("lab.earningPayment.table.testName")}</th>
                        <th>{t("lab.earningPayment.table.testType")}</th>
                        <th>
                          {t(
                            "lab.earningPayment.table.transactionAmount",
                            "Transaction Amount",
                          )}
                        </th>
                        <th>{t("lab.earningPayment.table.paymentMethod")}</th>
                        <th>{t("lab.earningPayment.table.orderStatus")}</th>
                        <th>{t("lab.earningPayment.table.paymentStatus")}</th>
                      </tr>
                    </thead>

                    <tbody>
                      {loadingEarnings ? (
                        <tr>
                          <td
                            colSpan="10"
                            style={{ textAlign: "center", padding: "20px" }}
                          >
                            <Loader />
                          </td>
                        </tr>
                      ) : earningsData.length === 0 ? (
                        <tr>
                          <td
                            colSpan="10"
                            style={{ textAlign: "center", padding: "20px" }}
                          >
                            {t("lab.earningPayment.noData")}
                          </td>
                        </tr>
                      ) : (
                        earningsData.map((item, idx) => (
                          <tr key={item.bookingId || idx}>
                            <td>{item.bookingNumber}</td>
                            <td>{item.patientName}</td>
                            <td>{formatDate(item.serviceDate)}</td>
                            <td>{formatTime(item.serviceTime)}</td>
                            <td>{item.testName}</td>

                            {/* Test Type */}
                            <td>
                              <span
                                className={`${styles["db-status"]} ${styles[getStatusColor(item.testType)]}`}
                              >
                                {item.testType}
                              </span>
                            </td>

                            {/* Transaction Amount — total the patient paid */}
                            <td>{formatCurrency(item.totalPrice)}</td>

                            <td>{formatPaymentMethod(item.paymentMethod)}</td>

                            {/* Order Status */}
                            <td>
                              <span
                                className={`${styles["db-status"]} ${styles[getStatusColor(item.orderStatus)]}`}
                              >
                                {formatStatusLabel(item.orderStatus)}
                              </span>
                            </td>

                            {/* Payment Status */}
                            <td>
                              <span
                                className={`${styles["db-status"]} ${styles[getStatusColor(item.paymentStatus)]}`}
                              >
                                {formatStatusLabel(item.paymentStatus)}
                              </span>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className={styles["apt-pagination"]}>
                  <div className={styles["apt-pagination-info"]}>
                    {t("lab.earningPayment.pagination.displaying")}{" "}
                    {(currentPage - 1) * itemsPerPage + 1}{" "}
                    {t("lab.earningPayment.pagination.to")}{" "}
                    {Math.min(currentPage * itemsPerPage, totalEarnings)}{" "}
                    {t("lab.earningPayment.pagination.outOf")} {totalEarnings}
                  </div>
                  <div className={styles["apt-pagination-controls"]}>
                    <button
                      className={`${styles["apt-pagination-btn"]} ${
                        currentPage === 1 ? styles["disabled"] : ""
                      }`}
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1 || loadingEarnings}
                    >
                      {t("lab.earningPayment.pagination.previous")}
                    </button>
                    <div className={styles["apt-page-numbers"]}>
                      {Array.from(
                        { length: Math.min(5, totalPages) },
                        (_, i) => {
                          const startPage = Math.max(1, currentPage - 2);
                          const page = startPage + i;
                          if (page > totalPages) return null;
                          return (
                            <button
                              key={page}
                              className={`${styles["apt-page-btn"]} ${
                                currentPage === page ? styles["active"] : ""
                              }`}
                              onClick={() => handlePageChange(page)}
                              disabled={loadingEarnings}
                            >
                              {page}
                            </button>
                          );
                        },
                      )}
                    </div>
                    <button
                      className={`${styles["apt-pagination-btn"]} ${
                        currentPage === totalPages ? styles["disabled"] : ""
                      }`}
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages || loadingEarnings}
                    >
                      {t("lab.earningPayment.pagination.next")}
                    </button>
                  </div>
                </div>
              </>
            )}

            {activeTab === "Withdrawals" && (
              <>
                {/* Filters */}
                <div className={styles["apt-filters"]}>
                  <div className={styles["apt-filter-group"]}>
                    <div className={styles["apt-search-wrapper"]}>
                      <Search className={styles["apt-search-icon"]} />
                      <input
                        type="text"
                        placeholder={t(
                          "lab.earningPayment.withdrawals.searchPlaceholder",
                          "Search reference or amount...",
                        )}
                        value={withdrawalSearch}
                        onChange={(e) => setWithdrawalSearch(e.target.value)}
                        className={styles["apt-search-input"]}
                      />
                    </div>
                    <select
                      className={styles["apt-status-filter"]}
                      value={withdrawalStatusFilter}
                      onChange={(e) =>
                        setWithdrawalStatusFilter(e.target.value)
                      }
                    >
                      <option value="">
                        {t(
                          "lab.earningPayment.withdrawals.requestStatus",
                          "Request Status",
                        )}
                      </option>
                      <option value="ALL">
                        {t(
                          "lab.earningPayment.filters.allStatus",
                          "All Status",
                        )}
                      </option>
                      <option value="pending">
                        {t(
                          "lab.earningPayment.withdrawals.statusRequested",
                          "Requested",
                        )}
                      </option>
                      <option value="approved">
                        {t(
                          "lab.earningPayment.withdrawals.statusProcessing",
                          "Processing",
                        )}
                      </option>
                      <option value="completed">
                        {t(
                          "lab.earningPayment.withdrawals.statusCompleted",
                          "Completed",
                        )}
                      </option>
                      <option value="rejected">
                        {t(
                          "lab.earningPayment.withdrawals.statusFailed",
                          "Failed",
                        )}
                      </option>
                      <option value="cancelled">
                        {t(
                          "lab.earningPayment.withdrawals.statusCancelled",
                          "Cancelled",
                        )}
                      </option>
                    </select>
                    <select
                      className={styles["apt-status-filter"]}
                      value={withdrawalMethodFilter}
                      onChange={(e) =>
                        setWithdrawalMethodFilter(e.target.value)
                      }
                    >
                      <option value="">
                        {t(
                          "lab.earningPayment.withdrawals.paymentMethod",
                          "Payment Method",
                        )}
                      </option>
                      <option value="ALL">
                        {t(
                          "lab.earningPayment.withdrawals.allMethods",
                          "All Methods",
                        )}
                      </option>
                      <option value="zain_cash">Zain Cash</option>
                      <option value="qi_card">Qi Card</option>
                      <option value="wire_transfer">Wire Transfer</option>
                      <option value="pay_billing">PayBilling</option>
                    </select>
                    <div className={styles["date-wrapper"]}>
                      <DatePicker
                        key={`wd-datepicker-${withdrawalDate ? withdrawalDate.getTime() : "empty"}`}
                        className={styles["apt-date-filter"]}
                        selected={withdrawalDate}
                        onChange={(d) => setWithdrawalDate(d)}
                        placeholderText={t(
                          "lab.earningPayment.filters.selectDate",
                        )}
                      />
                    </div>
                    <button
                      className={styles["apt-apply-btn"]}
                      onClick={clearWithdrawalFilters}
                      disabled={loadingWithdrawals}
                      style={{ marginLeft: "8px", backgroundColor: "#6c757d" }}
                    >
                      {t("lab.earningPayment.filters.clearFilters")}
                    </button>
                  </div>
                </div>

                <div
                  className={styles["apt-appointments-table"]}
                  style={{ marginTop: "0.5rem" }}
                >
                  <table>
                    <thead>
                      <tr>
                        <th>
                          {t(
                            "lab.earningPayment.withdrawals.dateRequested",
                            "Date Requested",
                          )}
                        </th>
                        <th>
                          {t("lab.earningPayment.withdrawals.type", "Type")}
                        </th>
                        <th>
                          {t(
                            "lab.earningPayment.withdrawals.bankAccount",
                            "Bank Account",
                          )}
                        </th>
                        <th>
                          {t(
                            "lab.earningPayment.withdrawals.amountWithdrawn",
                            "Amount Withdrawn",
                          )}
                        </th>
                        <th>
                          {t(
                            "lab.earningPayment.withdrawals.remainingBalance",
                            "Remaining Balance",
                          )}
                        </th>
                        <th>
                          {t(
                            "lab.earningPayment.withdrawals.requestStatus",
                            "Request Status",
                          )}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {loadingWithdrawals ? (
                        <tr>
                          <td
                            colSpan="6"
                            style={{ textAlign: "center", padding: "20px" }}
                          >
                            <Loader />
                          </td>
                        </tr>
                      ) : settlementRecords.length === 0 ? (
                        <tr>
                          <td
                            colSpan="6"
                            style={{ textAlign: "center", padding: "20px" }}
                          >
                            {t(
                              "lab.earningPayment.withdrawals.noData",
                              "No withdrawal records found",
                            )}
                          </td>
                        </tr>
                      ) : (
                        settlementRecords.map((s) => {
                          const label = withdrawalStatusLabel(s.status);
                          // s.net_balance is the balance snapshot frozen at request time (BEFORE
                          // this withdrawal). "Remaining Balance" should reflect what is left
                          // AFTER the withdrawal, so deduct the settled amount. A Pay-In moves the
                          // (negative) balance toward zero instead.
                          const remainingBalance =
                            s.direction === "pay_in"
                              ? Number(s.net_balance || 0) +
                                Number(s.settlement_amount || 0)
                              : Number(s.net_balance || 0) -
                                Number(s.settlement_amount || 0);
                          return (
                            <tr key={s.id}>
                              <td>
                                {s.created_at ? formatDate(s.created_at) : "—"}
                              </td>
                              <td>
                                {/* Direction: Pay Out = Tiryaq → center (a
                                    withdrawal); Pay In = center → Tiryaq. */}
                                <span
                                  style={{
                                    fontWeight: 600,
                                    color:
                                      s.direction === "pay_in"
                                        ? "#EC003F"
                                        : "#16a34a",
                                  }}
                                >
                                  {/* `direction` is stored from the admin's view;
                                      invert it for the center: admin PAY_IN =
                                      center Pay Out, admin PAY_OUT = center Pay In. */}
                                  {s.direction === "pay_in"
                                    ? t(
                                        "lab.earningPayment.withdrawals.payOut",
                                        "Pay Out",
                                      )
                                    : t(
                                        "lab.earningPayment.withdrawals.payIn",
                                        "Pay In",
                                      )}
                                </span>
                              </td>
                              <td>
                                {withdrawalAccountLabel(s.account_details)}
                              </td>
                              <td
                                style={{
                                  color:
                                    s.direction === "pay_in"
                                      ? "#EC003F"
                                      : "#16a34a",
                                }}
                              >
                                {s.direction === "pay_in" ? "- " : ""}IQD{" "}
                                {formatWithdrawAmount(s.settlement_amount)}
                              </td>
                              <td>
                                IQD {formatWithdrawAmount(remainingBalance)}
                              </td>
                              <td>
                                <span
                                  className={`${styles["db-status"]} ${styles[withdrawalStatusClass(s.status)]}`}
                                >
                                  {label}
                                </span>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className={styles["apt-pagination"]}>
                  <div className={styles["apt-pagination-info"]}>
                    {t("lab.earningPayment.pagination.displaying")}{" "}
                    {withdrawalsTotal === 0
                      ? 0
                      : (withdrawalsPage - 1) * itemsPerPage + 1}{" "}
                    {t("lab.earningPayment.pagination.to")}{" "}
                    {Math.min(withdrawalsPage * itemsPerPage, withdrawalsTotal)}{" "}
                    {t("lab.earningPayment.pagination.outOf")}{" "}
                    {withdrawalsTotal}
                  </div>
                  <div className={styles["apt-pagination-controls"]}>
                    <button
                      className={`${styles["apt-pagination-btn"]} ${
                        withdrawalsPage === 1 ? styles["disabled"] : ""
                      }`}
                      onClick={() =>
                        setWithdrawalsPage((p) => Math.max(1, p - 1))
                      }
                      disabled={withdrawalsPage === 1 || loadingWithdrawals}
                    >
                      {t("lab.earningPayment.pagination.previous")}
                    </button>
                    <div className={styles["apt-page-numbers"]}>
                      {Array.from(
                        { length: Math.min(5, withdrawalsTotalPages) },
                        (_, i) => {
                          const startPage = Math.max(1, withdrawalsPage - 2);
                          const page = startPage + i;
                          if (page > withdrawalsTotalPages) return null;
                          return (
                            <button
                              key={page}
                              className={`${styles["apt-page-btn"]} ${
                                withdrawalsPage === page ? styles["active"] : ""
                              }`}
                              onClick={() => setWithdrawalsPage(page)}
                              disabled={loadingWithdrawals}
                            >
                              {page}
                            </button>
                          );
                        },
                      )}
                    </div>
                    <button
                      className={`${styles["apt-pagination-btn"]} ${
                        withdrawalsPage === withdrawalsTotalPages
                          ? styles["disabled"]
                          : ""
                      }`}
                      onClick={() =>
                        setWithdrawalsPage((p) =>
                          Math.min(withdrawalsTotalPages, p + 1),
                        )
                      }
                      disabled={
                        withdrawalsPage === withdrawalsTotalPages ||
                        loadingWithdrawals
                      }
                    >
                      {t("lab.earningPayment.pagination.next")}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </>
      )}
    </LabDashboardLayout>
  );
};

export default LabEarningPayment;
